/**
 * Created by james on 6/4/14.
 */
"use strict";

// ---------------------------------------------------
// Dependencies

var _ = require('underscore');
var Clump = require('bunsen/libraries/clump');
var bunsenLib = require('bunsen/libraries/bunsen');
var serverVars = require('server-vars');
var getDeferredMethods = require('bunsen/helpers/getDeferredMethods');
var requ = require('reku');
var assert = require('bunsen/helpers/assert');

// ---------------------------------------------------
// Local Vars

var deferred = getDeferredMethods().Deferred;
var when = getDeferredMethods().when;

// ---------------------------------------------------
// Module Definition

var root = typeof window === 'undefined' ? global : window;
root.bunsen = root.bunsen || {};

/**
 * @class DataProviderHelper
 * @inner
 */
var DataProviderHelper = function () {
    this._clumper = new Clump();
    this._promises = {};
    this._data = _.extend({}, serverVars);
    this._configs = [];
    this.shouldUseClump = true;
    this.addTokenToRequests = true;
};

/**
 *
 * SEE THIS GIST FOR MORE DETAILED USAGE:
 * https://gist.github.com/jballant/94378c1337b225cc7aa4
 *
 * var dataProvider = require('dataProvider');
 *
 * dataProvider = dataProvider();
 * dataProvider.addAsyncConfigs([
 *  {
 *    KEY: 'FOO',
 *    url: 'soa/endpoint',
 *    queryParams: {
 *      foo: 123
 *    }
 *  },
 *  {
 *     KEY: 'KEY_NAME'
 *     makeRequest: function (clump) {
 *          return GeoData().fetch({ clump: clump });
 *     },
 *     getDataOnResolve: function () {
 *          return geoData
 *     }
 *  }])
 *
 *  dataProvider.fetchAsyncData().done(function () {
 *      view.render().appendTo('body');
 *  });;
 *
 *  View.render = function () {
 *      var str = dataProvider.get('KEY_NAME');
 *      this.$el.html(str);
 *  }
 *
 */

_.extend(DataProviderHelper.prototype, /** @lends DataProviderHelper.prototype */{

    _transport: requ,

    /**
     * @type {function(new:DataProviderHelper)}
     */
    Construct: DataProviderHelper,

    /**
     * Add configurations for async data.
     *
     * See addAsyncConfig docs for more info
     * @public
     * @param configs
     * @returns {DataProviderHelper}
     */
    addAsyncConfigs: function (configs) {
        _.each(configs, this.addAsyncConfig, this);
        return this;
    },

    /**
     * Add a configuration for async data (data
     * that needs to be retrieved from the server
     * and doesn't already exist on serverVars).
     *
     * @public
     * @param {object} config
     *
     * @param {string} config.KEY
     *      The key needed to access the data, if it
     *      already exists,
     * @param {string} [config.url]
     *      The url to retrieve the data from, if
     *      you want the dataProvider to handle fetching
     *      the data
     * @param {object} [config.queryParams]
     *      The query params you want to add to the
     *      url, if you want the dataProvider to handle
     *      fetching the data
     * @param {string} [config.resultKey]
     *      If the dataProvider is handling the data
     *      fetching, the response may have the actual
     *      object you want to use as your data in a
     *      sub-object of 'result'. Specify that key
     *      with this option.
     * @param {function} [config.makeRequest]
     *      If you want dataProvider to delegate
     *      making the request to a custom function,
     *      use this option. Your callback will be
     *      passed a clump object you can add a
     *      request to
     * @param {function} [config.getDataOnResolve]
     *      Once a request has resolved, you can have
     *      the dataProvider delegate determining
     *      what that object is with this callback,
     *      your function should return what you want
     *      to be used as the data.
     * @param {function} [config.context]
     *      For the makeRequest and getDataOnResolve
     *      functions, you can specify a context for
     *      these callbacks with this option.
     *
     * @returns {DataProviderHelper}
     */
    addAsyncConfig: function (config) {
        if (!config.KEY) {
            console.error('dataProvider: KEY is required for an async config: ', config);
        }
        if (this._data[config.KEY]) {
            console.log('dataProvider: Skipping async config. Data for key ' + config.KEY + ' already exists:', config);
            return this;
        }
        var existingConfig = this.getConfigForKey(config.KEY);
        if (existingConfig) {
            if (existingConfig !== config) {
                console.error('dataProvider: Cannot add new config. Config with key ' + existingConfig.KEY + ' already exists:', [config, existingConfig]);
            }
            return this;
        }
        this._configs.push(config);
        return this;
    },

    asyncDataMakeXHR: function (options) {
        options = _.extend({
            type: 'GET',
            dataType: 'json'
        }, options);
        if (this.shouldUseClump) {
            return this._clumper.add(options);
        }
        return this._transport(options);
    },

    /**
     * Returns data matching a key. Note that
     * you can use 'dot' notation to retrieve
     * nested objects, and if a parent object
     * is null or undefined, then null/undefined
     * will be returned.
     * @public
     * @param {string} key
     * @returns {*}
     */
    get: function (key) {
        assert(typeof key === 'string', 'key must be a string');
        var segs = key.split('.');
        var seg = segs.shift();
        var data = this._data;
        while (seg) {
            data = data[seg];
            seg = segs.shift();
            if (seg && (!data || typeof data !== 'object')) {
                return undefined;
            }
        }
        return data;
    },

    /**
     * @public
     * @param {string} key
     * @returns {object}
     */
    getConfigForKey: function (key) {
        return _.findWhere(this._configs, { KEY: key });
    },

    /**
     * @public
     * @param {string} key
     * @param {object} data
     * @param {object} options
     * @returns {DataProviderHelper}
     */
    setDataForKey: function (key, data, options) {
        options = options || {};
        var config;

        if (options.parse) {
            config = this.getConfigForKey(key);
            if (config) {
                data = this.parseResultForConfig(config, data);
            }
        }

        this._data[key] = data;
        return this;
    },

    getPromiseForKey: function (key) {
        return this._promises[key];
    },

    setPromiseForKey: function (key, promise) {
        this._promises[key] = promise;
        return this;
    },

    setClump: function (clump) {
        this._clumper = clump;
        return this;
    },

    getClump: function () {
        return this._clumper;
    },

    isFetchingAsyncData: function () {
        var proms = this._promises;
        var i = 0;
        var len = this._promises.length;
        for (i; i < len; i++) {
            if (proms[i].state() === 'pending') {
                return true;
            }
        }
        return false;
    },

    parseResultForConfig: function (config, response) {
        if (config && _.isFunction(config.getDataOnResolve)) {
            return config.getDataOnResolve.call(config.context, response);
        }
        var rawResult = response ? response.result : null;
        if (rawResult && config.resultKey) {
            return rawResult[config.resultKey];
        }
        return rawResult;
    },

    /**
     * Fetch all data that has been registered
     * as an async data dependency with
     * addAsyncConfig/addAsyncConfigs. Makes a
     * clump call automatically by default, but
     * a custom clump instance can be provided.
     * @public
     * @param {object} options
     * @param {Clump} [options.clump]
     * @param {boolean} [options.runClump=true]
     * @returns {Deferred.promise}
     */
    fetchAsyncData: function (options) {
        options = options || {};
        var dfd = deferred();
        if (options.clump) {
            this.setClump(options.clump);
        }
        _.each(this._configs, function (config) {
            this.fetchAsyncDataForKey(config.KEY);
        }, this);
        if (this.shouldUseClump && options.runClump !== false) {
            this._clumper.run();
        }
        when.apply(null, _.values(this._promises)).done(function () {
            dfd.resolve.apply(dfd, arguments);
        }).fail(function () {
            dfd.reject.apply(dfd, arguments);
        });
        return dfd.promise();
    },

    /**
     * @public
     * @param {string} key
     * @returns {Deferred.promise}
     */
    fetchAsyncDataForKey: function (key) {
        var config = this.getConfigForKey(key);
        var dfd = deferred();
        var self = this;
        var token = this.addTokenToRequests ? bunsenLib.getToken() : {};
        var xhr;
        var ajaxOpts;
        var queryParamData;
        var prom;

        if (this.get(key)) {
            return deferred().resolve(this.get(key)).promise();
        }

        if (!config) {
            return null;
        }

        // Call custom make request function if present, passing clump instance
        if (_.isFunction(config.makeRequest)) {
            xhr = config.makeRequest.call(config.context, self.shouldUseClump ? self._clumper : null);
        } else {
            // get the query params for the ajax request
            // user is allowed to make queryParams a function
            // that returns an object of query params
            queryParamData = _.isFunction(config.queryParams) ? config.queryParams.call(config.context) : config.queryParams;
            // combine them with the user token
            queryParamData = _.extend({}, token, queryParamData);
            // Otherwise use the config to make an ajax request
            ajaxOpts = {
                url: config.url,
                data: queryParamData
            };
            xhr = self.asyncDataMakeXHR(ajaxOpts);
        }

        if (xhr) {
            xhr
                .done(function (response) {
                    var result = self.parseResultForConfig(config, response);
                    self.setDataForKey(key, result);
                    dfd.resolve(result);
                })
                .fail(function () {
                    dfd.reject.apply(dfd, arguments);
                });
            prom = dfd.promise().always(function () {
                self.setPromiseForKey(key, null);
            });
            self.setPromiseForKey(key, prom);
            return prom;
        }
        return null;
    }

});

/**
 * @module dataProvider
 * @type {DataProviderHelper}
 */
module.exports = root.bunsen.DP = new DataProviderHelper();



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/helpers/dataProvider.js
// module id = 780
// module chunks = 64